import React, { forwardRef } from 'react';

import { ButtonMenuItem as AtlasButtonMenuItem } from '@atlaskit/side-nav-items/button-menu-item';

import { ListItem } from '../list-item';
import { MenuItemBase } from '../menu-item';
import type {
	MenuItemCommonProps,
	MenuItemLinkOrButtonCommonProps,
	MenuItemOnClick,
	MenuItemSlots,
} from '../menu-item/types';

/**
 * We intentionally do not support the `isSelected` prop (which other menu item components
 * support) because `ButtonMenuItem`s do not correspond to a "page", so can't be navigated
 *  to and become in a selected state.
 */
export type ButtonMenuItemProps = MenuItemLinkOrButtonCommonProps & {
	/**
	 * We are not using a discriminated union to enforce that the `actions` and `actionsOnHover`
	 * props are not used when `isDisabled` is true due to ergonomic type issues with `boolean`
	 * types (as oppposed to literal `true` or `false` types), e.g. if a conditional boolean
	 * variable is passed to `isDisabled`.
	 */
	/**
	 * Whether the menu item is disabled.
	 *
	 * When disabled, content in the `actions` and `actionsOnHover` props will not be rendered.
	 *
	 * The menu item will not be interactive and will not respond to hover or focus.
	 */
	isDisabled?: boolean;
	/**
	 * Called when the user has clicked on the trigger content.
	 */
	onClick?: MenuItemOnClick<HTMLButtonElement>;
	/**
	 * Whether the ADS navigation (left-nav) is enabled rather than the in-component navigation
	 */
	isADSNavigationEnabled: boolean;
};

/**
 * __ButtonMenuItem__
 *
 * A menu item button. It should be used within a `ul`, as it renders a list item.
 */
export const ButtonMenuItem: React.ForwardRefExoticComponent<
	MenuItemCommonProps &
		MenuItemSlots & {
			description?: React.ReactNode;
			listItemRef?: React.Ref<HTMLDivElement>;
		} & {
			/**
			 * We are not using a discriminated union to enforce that the `actions` and `actionsOnHover`
			 * props are not used when `isDisabled` is true due to ergonomic type issues with `boolean`
			 * types (as oppposed to literal `true` or `false` types), e.g. if a conditional boolean
			 * variable is passed to `isDisabled`.
			 */
			/**
			 * Whether the menu item is disabled.
			 *
			 * When disabled, content in the `actions` and `actionsOnHover` props will not be rendered.
			 *
			 * The menu item will not be interactive and will not respond to hover or focus.
			 */
			isDisabled?: boolean;
			/**
			 * Called when the user has clicked on the trigger content.
			 */
			onClick?: MenuItemOnClick<HTMLButtonElement>;
			/**
			 * Whether the ADS navigation (left-nav) is enabled rather than the in-component navigation
			 */
			isADSNavigationEnabled: boolean;
		} & React.RefAttributes<HTMLButtonElement>
> = forwardRef<HTMLButtonElement, ButtonMenuItemProps>(
	(
		{
			testId,
			actions,
			children,
			description,
			elemAfter,
			isDisabled,
			elemBefore,
			actionsOnHover,
			onClick,
			interactionName,
			isContentTooltipDisabled,
			visualContentRef,
			listItemRef,
			level = 0,
			isADSNavigationEnabled,
		},
		forwardedRef,
	) => {
		const commonProps = {
			testId,
			description,
			elemAfter,
			isDisabled,
			actions: isDisabled ? undefined : actions,
			actionsOnHover: isDisabled ? undefined : actionsOnHover,
			onClick,
			ref: forwardedRef,
			visualContentRef,
			interactionName,
			isContentTooltipDisabled,
		};

		return isADSNavigationEnabled ? (
			<AtlasButtonMenuItem
				{...commonProps}
				description={typeof description === 'string' ? description : undefined}
				elemBefore={elemBefore as React.ReactNode}
			>
				{children}
			</AtlasButtonMenuItem>
		) : (
			<ListItem ref={listItemRef}>
				<MenuItemBase {...commonProps} level={level} elemBefore={elemBefore}>
					{children}
				</MenuItemBase>
			</ListItem>
		);
	},
);
